import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';
import type {
    FinancialOverviewChartEvent,
    FinancialOverviewChartRange,
    FinancialOverviewTickerDataPoint,
} from '@/shared/components/financial-overview/types';

const HISTORICAL_SERIES_STALE_TIME = 5 * 60 * 1000;
const REFERENCE_DATA_STALE_TIME = 15 * 60 * 1000;
const REAL_TIME_SERIES_STALE_TIME = 15 * 1000;

interface FinancialOverviewMarketSeriesQuery {
    chartRange: FinancialOverviewChartRange;
    from: string;
    symbol: string;
    to: string;
}

const normalizeSymbol = (symbol: string) => symbol.trim().toUpperCase();

const resolveList = (value: unknown): Record<string, unknown>[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'));
    }

    if (!value || typeof value !== 'object') {
        return [];
    }

    const record = value as Record<string, unknown>;

    return resolveList(record.historical ?? record.data);
};

const resolveOptionalString = (value: unknown) =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

const resolveOptionalNumber = (value: unknown) => {
    const number = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;

    return Number.isFinite(number) ? number : undefined;
};

const fetchMarketSeries = async (
    { chartRange, from, symbol, to }: FinancialOverviewMarketSeriesQuery,
    signal: AbortSignal,
) => {
    try {
        const normalizedSymbol = normalizeSymbol(symbol);
        const endpoint =
            chartRange === 'realTime'
                ? `/api/vendors/fmp/charts/intraday/1min/${normalizedSymbol}`
                : `/api/vendors/fmp/charts/eod/${normalizedSymbol}`;
        const response = await authorizedAxiosInstance.get<FinancialOverviewTickerDataPoint[]>(endpoint, {
            params: chartRange === 'realTime' ? undefined : { from, to },
            signal,
        });

        return Array.isArray(response.data) ? [...response.data].reverse() : [];
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

const fetchNewsEvents = async (symbol: string, signal: AbortSignal): Promise<FinancialOverviewChartEvent[]> => {
    try {
        const response = await authorizedAxiosInstance.get(
            `/api/vendors/fmp/companies/${normalizeSymbol(symbol)}/news`,
            { signal },
        );

        return resolveList(response.data)
            .map((item) => ({
                date: String(item.publishedDate ?? item.date ?? '').slice(0, 10),
                description: resolveOptionalString(item.text ?? item.content ?? item.summary),
                externalUrl: resolveOptionalString(item.url),
                label: 'News',
                source: resolveOptionalString(item.site ?? item.publisher),
                title: typeof item.title === 'string' ? item.title : undefined,
                type: 'news' as const,
            }))
            .filter((event) => Boolean(event.date));
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

const fetchDividendEvents = async (symbol: string, signal: AbortSignal): Promise<FinancialOverviewChartEvent[]> => {
    try {
        const response = await authorizedAxiosInstance.get(
            `/api/vendors/fmp/historical/dividends/${normalizeSymbol(symbol)}`,
            { signal },
        );

        return resolveList(response.data)
            .map((item) => ({
                amount: resolveOptionalNumber(item.adjDividend ?? item.dividend),
                currency: resolveOptionalString(item.currency),
                date: String(item.date ?? '').slice(0, 10),
                label: 'Dividend',
                paymentDate: resolveOptionalString(item.paymentDate),
                recordDate: resolveOptionalString(item.recordDate),
                title: 'Dividend distribution',
                type: 'dividends' as const,
            }))
            .filter((event) => Boolean(event.date));
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

export const financialOverviewQueryKeys = {
    dividends: (symbol: string) =>
        ['vendors', 'fmp', 'financial-overview', normalizeSymbol(symbol), 'dividends'] as const,
    marketSeries: ({ chartRange, from, symbol, to }: FinancialOverviewMarketSeriesQuery) =>
        [
            'vendors',
            'fmp',
            'financial-overview',
            normalizeSymbol(symbol),
            'market-series',
            chartRange,
            from,
            to,
        ] as const,
    news: (symbol: string) => ['vendors', 'fmp', 'financial-overview', normalizeSymbol(symbol), 'news'] as const,
};

export const financialOverviewQueryOptions = {
    dividends: (symbol: string) =>
        queryOptions({
            queryFn: ({ signal }) => fetchDividendEvents(symbol, signal),
            queryKey: financialOverviewQueryKeys.dividends(symbol),
            staleTime: REFERENCE_DATA_STALE_TIME,
        }),
    marketSeries: (query: FinancialOverviewMarketSeriesQuery) =>
        queryOptions({
            queryFn: ({ signal }) => fetchMarketSeries(query, signal),
            queryKey: financialOverviewQueryKeys.marketSeries(query),
            staleTime: query.chartRange === 'realTime' ? REAL_TIME_SERIES_STALE_TIME : HISTORICAL_SERIES_STALE_TIME,
        }),
    news: (symbol: string) =>
        queryOptions({
            queryFn: ({ signal }) => fetchNewsEvents(symbol, signal),
            queryKey: financialOverviewQueryKeys.news(symbol),
            staleTime: REFERENCE_DATA_STALE_TIME,
        }),
};
